import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'

const ENQUIRY_SEEN_STORAGE_KEY = 'salzburg_enquiry_seen'
const FEEDBACK_SEEN_STORAGE_KEY = 'salzburg_feedback_seen'
const NOTIFICATIONS_SEEN_STORAGE_KEY = 'salzburg_notifications_seen'

type DashboardBadgeContextType = {
  hasNewEnquiry: boolean
  hasNewFeedback: boolean
  hasNewNotification: boolean
  markEnquiryAsSeen: () => void
  markFeedbackAsSeen: () => void
  markNotificationsAsSeen: () => void
  triggerNewEnquiry: () => void
  triggerNewFeedback: () => void
  triggerNewNotification: () => void
  resetNewBadges: () => void
}

const DashboardBadgeContext = createContext<DashboardBadgeContextType | undefined>(undefined)

export const DashboardBadgeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const checkEnquiryBadge = () => {
    // If marked seen, false; otherwise if marked new or unseen, true
    if (localStorage.getItem(ENQUIRY_SEEN_STORAGE_KEY) === 'true') return false
    return true
  }

  const [hasNewEnquiry, setHasNewEnquiry] = useState<boolean>(checkEnquiryBadge)

  const [hasNewNotification, setHasNewNotification] = useState<boolean>(() => {
    return localStorage.getItem(NOTIFICATIONS_SEEN_STORAGE_KEY) !== 'true'
  })

  const markEnquiryAsSeen = useCallback(() => {
    setHasNewEnquiry(false)
    localStorage.setItem(ENQUIRY_SEEN_STORAGE_KEY, 'true')
    localStorage.setItem(FEEDBACK_SEEN_STORAGE_KEY, 'true')
  }, [])

  const markNotificationsAsSeen = useCallback(() => {
    setHasNewNotification(false)
    localStorage.setItem(NOTIFICATIONS_SEEN_STORAGE_KEY, 'true')
  }, [])

  const triggerNewEnquiry = useCallback(() => {
    setHasNewEnquiry(true)
    localStorage.removeItem(ENQUIRY_SEEN_STORAGE_KEY)
    localStorage.removeItem(FEEDBACK_SEEN_STORAGE_KEY)
  }, [])

  const triggerNewNotification = useCallback(() => {
    setHasNewNotification(true)
    localStorage.removeItem(NOTIFICATIONS_SEEN_STORAGE_KEY)
  }, [])

  const resetNewBadges = useCallback(() => {
    setHasNewEnquiry(true)
    setHasNewNotification(true)
    localStorage.removeItem(ENQUIRY_SEEN_STORAGE_KEY)
    localStorage.removeItem(FEEDBACK_SEEN_STORAGE_KEY)
    localStorage.removeItem(NOTIFICATIONS_SEEN_STORAGE_KEY)
  }, [])

  // Listen for storage or custom events fired when a user sends an enquiry
  useEffect(() => {
    const handleUpdate = () => {
      setHasNewEnquiry(checkEnquiryBadge())
      setHasNewNotification(localStorage.getItem(NOTIFICATIONS_SEEN_STORAGE_KEY) !== 'true')
    }

    window.addEventListener('dashboard-badges-updated', handleUpdate)
    window.addEventListener('salzburg-enquiry-created', handleUpdate)
    window.addEventListener('storage', handleUpdate)

    return () => {
      window.removeEventListener('dashboard-badges-updated', handleUpdate)
      window.removeEventListener('salzburg-enquiry-created', handleUpdate)
      window.removeEventListener('storage', handleUpdate)
    }
  }, [])

  return (
    <DashboardBadgeContext.Provider
      value={{
        hasNewEnquiry,
        hasNewFeedback: hasNewEnquiry,
        hasNewNotification,
        markEnquiryAsSeen,
        markFeedbackAsSeen: markEnquiryAsSeen,
        triggerNewEnquiry,
        triggerNewFeedback: triggerNewEnquiry,
        triggerNewNotification,
        resetNewBadges,
      }}
    >
      {children}
    </DashboardBadgeContext.Provider>
  )
}

export const useDashboardBadges = (): DashboardBadgeContextType => {
  const context = useContext(DashboardBadgeContext)
  if (!context) {
    throw new Error('useDashboardBadges must be used within a DashboardBadgeProvider')
  }
  return context
}
